import { useFormik } from "formik";
import ModalComponent from "..";
import { BaseApiErrorResponse, ModalCompProps } from "../../../utils/types";
import { AddAdminSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import { TextInput } from "../../inputs/textInput";
import { useAddAdminMutation } from "../../../redux/features/admin/addAdmin.api";
import ComboBox from "../../dropdown/comboBox";
import { CaretDown } from "../../icon/downcaret";
import { adminRoles } from "../../../utils/constants";
import { InfoComponent } from "../../notification/infoComponent";

export const AddAdminModal = ({
  open,
  handleClose,
  onSuccess,
  refetchAdmin,
}: ModalCompProps & {
  onSuccess: () => void;
  refetchAdmin: () => void;
}) => {
  const [addAdmin, { isLoading: addingAdmin, isError, error }] =
    useAddAdminMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    handleChange,
    resetForm,
    setFieldValue,
    isValid,
  } = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "validPassword@1",
      role: "",
    },
    enableReinitialize: true,
    validationSchema: AddAdminSchema,
    onSubmit: async (vals) => {
      const resp = await addAdmin(vals);
      if (resp?.data?.status === "success") {
        handleClose();
        onSuccess();
        refetchAdmin();
        resetForm();
      }
    },
  });

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
        resetForm();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      title={"Add Admin"}
      subtitle={undefined}
      showIcon={true}
      iconType="user-plus"
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {isError && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <TextInput
            type="text"
            name="firstName"
            value={values.firstName}
            placeholder="Enter first name"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="First name"
            errors={
              errors.firstName && touched.firstName ? errors.firstName : ""
            }
          />

          <TextInput
            type="text"
            name="lastName"
            value={values.lastName}
            placeholder="Enter last name"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Last name"
            errors={errors.lastName && touched.lastName ? errors.lastName : ""}
          />

          <TextInput
            type="text"
            name="email"
            value={values.email}
            placeholder="Enter email"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Email"
            errors={errors.email && touched.email ? errors.email : ""}
            containerClassName="col-span-full"
          />
          <TextInput
            type="password"
            name="password"
            value={values.password}
            placeholder="Enter New Password"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Enter New Password"
            errors={errors.password && touched.password ? errors.password : ""}
            autoFocus
          />
          <ComboBox
            menuItems={adminRoles.map((rol) => {
              return {
                name: rol.name,
                value: rol.value,
              };
            })}
            onSelectOption={(selected) => {
              setFieldValue("role", selected);
            }}
            menuClassName="!max-h-[200px] translate-x-[-12px]"
            position="top start"
            immediate
            type="text"
            name="category"
            value={adminRoles.find((r) => r.value === values.role)?.name ?? ""}
            placeholder={"Select Role"}
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title={"Role"}
            errors={errors.role && touched.role ? errors.role : ""}
            readOnly
            containerClassName="col-span-full"
            customIcon={<CaretDown />}
            customIconPosition="right"
          />

          <p className="col-span-full text-grey-500">
            If you don’t enter a password, the password would be set as
            <span className="font-bold"> validPassword@1.</span>
          </p>

          <div className="col-span-full flex items-center justify-end gap-2">
            <Button
              text={"Cancel"}
              type="button"
              onClick={() => {
                handleClose();
                resetForm();
              }}
              variation="plain-grey"
              className="max-w-fit"
            />
            <Button
              text={"Add Admin"}
              type="submit"
              className="max-w-fit"
              loading={addingAdmin}
              disabled={!isValid || addingAdmin}
              variation={"primary"}
            />
          </div>
        </div>
      </form>
    </ModalComponent>
  );
};
